(ns nosco.richtext.video-uploads
  (:require
   ["tus-js-client" :as tus]
   [ajax.core :as ajax]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.controllers :as controllers]
   [nosco.graphql-client :as gql-client]
   [nosco.hooks :as nosco-hooks]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.ui.dropzone :as dropzone]
   [nosco.ui.progress :as progress]
   [nosco.ui.toast :as toast]))

(def create-vimeo-link (gql-client/graphql-query "./Uploads.graphql" :CreateVimeoLink))
(def create-twentythree-upload-token (gql-client/graphql-query "./Uploads.graphql" :CreateTwentyThreeUploadToken))
(def query-twentythree-embed-code (gql-client/graphql-query "./Uploads.graphql" :QueryTwentyThreeVideoEmbedCode))

(defn get-vimeo-upload-link [filesize]
  (-> (gql-client/mutation-promise! create-vimeo-link {:filesize filesize})
      (.then (fn [r]
               {:upload-link (-> r :data :createVimeoUploadLinkTus :uploadLink)
                :embed-html (-> r :data :createVimeoUploadLinkTus :embedHtml)}))))

(defn get-twentythree-upload-token []
  (-> (gql-client/mutation-promise! create-twentythree-upload-token nil)
      (.then (fn [r]
               {:token (-> r :data :create23VideoUploadToken :token)
                :redeem-url (-> r :data :create23VideoUploadToken :redeemUrl)}))))

(defn get-twentythree-video-embed-code [video-id]
  (-> (gql-client/mutation-promise! query-twentythree-embed-code {:videoId video-id})
      (.then (fn [r]
               {:embed-code (-> r :data :get23VideoEmbedCode :embedCode)}))))

(defn uploading? [upload-item]
  (not (:uploaded? upload-item)))

(defn video-upload-state-reducer [state action]
  (let [id (:id action)]
    (case (:type action)
      :start-upload
      (assoc state id {:bytes-uploaded 0
                       :filename (:filename action)
                       :file-size (:file-size action)})

      :embed-html-created
      (if (get state id)
        (-> state
            (assoc-in [id :embed-html] (:embed-html action)))
        state)

      :progress
      (if (get state id)
        (-> state
            (assoc-in [id :bytes-uploaded] (:bytes-uploaded action))
            (assoc-in [id :bytes-total] (:bytes-total action)))
        state)

      :tus-instantiated
      (if (get state id)
        (assoc-in state [id :instance] (:instance action))
        state)

      :finish-upload
      (if (get state id)
        (assoc-in state [id :uploaded?] true)
        state)

      :fail-upload
      (if (get state id)
        (assoc-in state [id :error] (:error action))
        state)

      :retry
      (if (get state id)
        (update-in state [id] dissoc :error)
        state)

      :abort
      (dissoc state id))))

(defn use-video-upload-state []
  (let [[state dispatch] (hooks/useReducer video-upload-state-reducer {})]
    [state dispatch]))

(defnc VideoUploadDropzone [{:keys [handle-file-drop]}]
  [dropzone/Dropzone
   {:width "100%"
    :max-files 10
    :on-drop (fn [files]
               (doseq [f files]
                 (handle-file-drop f)))}])

(defn vimeo-upload-file [{:keys [file dispatch]}]
  (let [video-uuid (random-uuid)]
    (dispatch {:type :start-upload
               :id video-uuid
               :filename (.-name file)
               :file-size (.-size file)})
    (-> (get-vimeo-upload-link (.-size file))
        (.then
         (fn [{:keys [upload-link embed-html]}]
           (dispatch {:type :embed-html-created
                      :id video-uuid
                      :embed-html embed-html})
           (let [instance
                 (tus/Upload.
                  file
                  #js {;; We're using uploadUrl here instead of endpoint that you might see
                       ;; on tus-js-client docs examples.
                       ;; Reason: https://github.com/tus/tus-js-client/issues/154#issuecomment-495419548
                       :uploadUrl upload-link
                       :onSuccess #(dispatch {:type :finish-upload
                                              :id video-uuid})
                       :onError (fn [error]
                                  (dispatch {:type :fail-upload
                                             :error error
                                             :id video-uuid}))
                       :onProgress (fn [uploaded total]
                                     (dispatch {:type :progress
                                                :id video-uuid
                                                :bytes-uploaded uploaded
                                                :bytes-total total}))})]
             (dispatch {:type :tus-instantiated
                        :id video-uuid
                        :instance instance})
             (.start instance)))))))

;; N.B. The length of the request (bytes-total) is different to the filesize, because it
;; also includes form data boundaries
(defn twentythree-upload-file [{:keys [file dispatch]}]
  (let [video-uuid (random-uuid)]
    (dispatch {:type :start-upload
               :id video-uuid
               :filename (.-name file)
               :file-size (.-size file)})
    (-> (get-twentythree-upload-token)
        (.then (fn [{:keys [token redeem-url]}]
                 (let [form-data (doto (js/FormData.)
                                   (.append "upload_token" token)
                                   (.append "file" file))]
                   (ajax/POST redeem-url
                     {:body form-data
                      :progress-handler
                      (fn [^js event]
                        (dispatch {:type :progress
                                   :id video-uuid
                                   :bytes-uploaded (.-loaded event)
                                   :bytes-total (.-total event)}))
                      :handler (fn [video-id]
                                 (.then (get-twentythree-video-embed-code (js/parseInt video-id 10))
                                        (fn [{:keys [embed-code]}]
                                          (dispatch {:type :embed-html-created
                                                     :id video-uuid
                                                     :embed-html embed-code})
                                          (dispatch {:type :finish-upload
                                                     :id video-uuid}))))

                      :error-handler
                      (fn [error]
                        (dispatch {:type :fail-upload
                                   :error error
                                   :id video-uuid}))})))))))

(defn can-upload? []
  (#{"VIMEO" "TWENTYTHREE"} (:video (get @controllers/app-state :siteConfiguration))))

(defn upload-video [{:keys [file dispatch] :as args}]
  (let [site-config (get @controllers/app-state :siteConfiguration)
        vimeo-enabled? (= (:video site-config) "VIMEO")
        twentythree-enabled? (= (:video site-config) "TWENTYTHREE")]
    (cond
      vimeo-enabled? (vimeo-upload-file args)
      twentythree-enabled? (twentythree-upload-file args)
      :else
      (do
        (toast/show-message-error {:title (t/t "Cannot upload video")
                                   :actions [{:type :link
                                              :href "https://help.nos.co/hc/en-us/articles/115002981611-Add-images-videos-to-your-idea"
                                              :title (t/t "More info")}]
                                   :text (t/t "You need to upload your video in a sharing site (e.g. YouTube, Vimeo, MS Streams) and embed it instead")})
        (throw (ex-info "Video integration not enabled" {}))))))

(defnc VideoUpload
  "Packaged component that renders both the dropzone
  as well as the progress list for all uploading videos.

  Also handles logic for uploading videos."
  [{:keys [state dispatch]}]
  (let [upload-file
        (fn [file]
          (upload-video
           {:file file
            :dispatch dispatch}))
        abort-upload! (fn [state id]
                        (let [{:keys [instance uploaded?]} (get state id)]
                          (when (and (not uploaded?) instance)
                            (-> (.abort instance true)
                                (.catch
                                 (fn [e]
                                   ;; Log and ignore the error
                                   ;; Termination failed but there is not much we can do
                                   (.log js/console e)))))
                          (dispatch {:type :abort
                                     :id id})))]
    (nosco-hooks/use-unmount
     [state]
     (fn [-state]
       (doseq [[id] -state]
         (abort-upload! -state id))))
    [:<>
     [VideoUploadDropzone
      {:handle-file-drop upload-file}]
     [:div {:class "mt-spacing-32"}
      [progress/ProgressList
       (for [[id upload] state]
         [progress/UploadProgressItem
          {:filename (:filename upload)
           :filesize (:file-size upload)
           ;; Don't show the progress as 100%, because it takes a
           ;; while after the upload is finished to get the response
           :progress-value (if (= (:bytes-uploaded upload) (:bytes-total upload))
                             (dec (:bytes-uploaded upload))
                             (:bytes-uploaded upload))
           :progress-max (:bytes-total upload)
           :uploaded? (:uploaded? upload)
           :thumb-icon icons2/Play-large
           :error (:error upload)
           :on-cancel #(abort-upload! state id)
           :on-retry (fn []
                       (when-let [instance (:instance upload)]
                         (.start instance)
                         (dispatch {:type :retry
                                    :id id})))}])]]]))
